/**
 * Example Component: Document Upload (for BGV, Leave, etc.)
 * Demonstrates batch upload with file validation
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilePresent from '@mui/icons-material/FilePresent';
import { useSelector } from 'react-redux';
import useBlobStorage from '../hooks/useBlobStorage';
import {
  BlobCategory,
  FILE_SIZE_LIMITS,
  ALLOWED_MIME_TYPES,
} from '../api/azureBlobStorageApi';

interface DocumentFile {
  id: string;
  name: string;
  url: string;
  blobName: string;
  size: number;
  uploadedAt: string;
  status: 'active' | 'deleting';
}

interface DocumentUploadProps {
  category: BlobCategory;
  containerName: string;
  entityType: string;
  entityId: string;
  title?: string;
  maxFiles?: number;
  acceptedMimeTypes?: string[];
  maxFileSize?: number;
  onDocumentsChange?: (documents: DocumentFile[]) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  category,
  containerName,
  entityType,
  entityId,
  title = 'Documents',
  maxFiles = 10,
  acceptedMimeTypes = ALLOWED_MIME_TYPES.DOCUMENTS,
  maxFileSize = FILE_SIZE_LIMITS.DOCUMENTS,
  onDocumentsChange,
}) => {
  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedDocumentForDelete, setSelectedDocumentForDelete] =
    useState<DocumentFile | null>(null);

  const tenantId = useSelector((state: any) => state.auth?.user?.tenantId);

  const { uploadFile, deleteFile, listFiles, loading, error, clearError } =
    useBlobStorage(category);

  // Load existing documents
  const handleLoadDocuments = async () => {
    try {
      const existingDocs = await listFiles(entityType, entityId);
      // Ensure existingDocs is always an array
      const docsArray = Array.isArray(existingDocs) ? existingDocs : [];
      const formatted = docsArray.map((doc, index) => ({
        id: `${Date.now()}-${index}`,
        name: doc.name.split('/').pop() || 'Unknown',
        url: doc.url,
        blobName: doc.name,
        size: doc.size,
        uploadedAt: doc.createdAt,
        status: 'active' as const,
      }));
      setDocuments(formatted);
      onDocumentsChange?.(formatted);
    } catch (err) {
      console.error('Error loading documents:', err);
      setDocuments([]);
    }
  };

  React.useEffect(() => {
    handleLoadDocuments();
  }, [entityId]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.currentTarget.files;
    if (!files || files.length === 0) return;

    clearError();

    // Validate file count
    if (documents.length + files.length > maxFiles) {
      alert(
        `Maximum ${maxFiles} files allowed. Currently have ${documents.length}`
      );
      return;
    }

    setUploading(true);

    const fileArray = Array.from(files);

    for (const file of fileArray) {
      // Validate file type
      if (!acceptedMimeTypes.includes(file.type)) {
        alert(`File type not allowed: ${file.name} (${file.type})`);
        continue;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        alert(
          `File ${file.name} exceeds ${(maxFileSize / 1024 / 1024).toFixed(2)}MB limit`
        );
        continue;
      }

      try {
        const response = await uploadFile(file, entityType, entityId);

        if (response?.success) {
          const newDoc: DocumentFile = {
            id: `${Date.now()}-${Math.random()}`,
            name: file.name,
            url: response.fileUrl || '',
            blobName: response.blobName || '',
            size: file.size,
            uploadedAt: new Date().toISOString(),
            status: 'active',
          };

          setDocuments((prev) => {
            const updated = [...prev, newDoc];
            onDocumentsChange?.(updated);
            return updated;
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
        alert(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);

    // Reset input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDeleteClick = (doc: DocumentFile) => {
    setSelectedDocumentForDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDocumentForDelete) return;

    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === selectedDocumentForDelete.id
          ? { ...doc, status: 'deleting' }
          : doc
      )
    );

    try {
      const success = await deleteFile(selectedDocumentForDelete.blobName);

      if (success) {
        setDocuments((prev) => {
          const updated = prev.filter(
            (doc) => doc.id !== selectedDocumentForDelete.id
          );
          onDocumentsChange?.(updated);
          return updated;
        });
      } else {
        // Restore status
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === selectedDocumentForDelete.id
              ? { ...doc, status: 'active' }
              : doc
          )
        );
        alert('Failed to delete document');
      }
    } catch (err) {
      alert('An error occurred while deleting');
    }

    setDeleteConfirmOpen(false);
    setSelectedDocumentForDelete(null);
  };

  return (
    <Card>
      <CardHeader
        title={title}
        avatar={<FilePresent />}
        action={
          <Chip
            label={`${documents.length}/${maxFiles}`}
            color={documents.length >= maxFiles ? 'error' : 'default'}
            size="small"
          />
        }
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Error Message */}
          {error && (
            <Alert severity="error" onClose={clearError}>
              {error}
            </Alert>
          )}

          {/* Upload Button */}
          <Box>
            <input
              type="file"
              id="doc-input"
              multiple
              accept={acceptedMimeTypes.join(',')}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={uploading || loading || documents.length >= maxFiles}
            />
            <label htmlFor="doc-input">
              <Button
                variant="contained"
                component="span"
                startIcon={
                  uploading || loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                disabled={uploading || loading || documents.length >= maxFiles}
                fullWidth
              >
                {uploading ? 'Uploading...' : 'Upload Documents'}
              </Button>
            </label>
          </Box>

          {/* Documents List */}
          {documents.length > 0 ? (
            <List>
              {documents.map((doc) => (
                <ListItem
                  key={doc.id}
                  sx={{
                    opacity: doc.status === 'deleting' ? 0.5 : 1,
                    backgroundColor:
                      doc.status === 'deleting'
                        ? '#ffebee'
                        : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={doc.name}
                    secondary={`${(doc.size / 1024).toFixed(2)}KB • ${new Date(doc.uploadedAt).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteClick(doc)}
                      disabled={doc.status === 'deleting' || loading}
                      title="Delete"
                    >
                      {doc.status === 'deleting' ? (
                        <CircularProgress size={24} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              No documents uploaded yet
            </Typography>
          )}

          {/* Info */}
          <Typography variant="caption" color="textSecondary">
            Max file size: {(maxFileSize / 1024 / 1024).toFixed(2)}MB
          </Typography>
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedDocumentForDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DocumentUpload;
